import React, { useMemo, useState } from 'react';
import { BookOpen, Search, X } from 'lucide-react';
import { cn, logInteraction } from '../lib/utils';
import { GLOSSARY, TRADING_TERM_CATEGORIES, TradingTermCategory } from '../constants';
import { BrainIcon, OwlIcon, ElephantIcon, CompassIcon } from './AIIcons';

type CategoryFilter = 'All' | TradingTermCategory;

const CATEGORY_DESCRIPTIONS: Record<TradingTermCategory, string> = {
  'Adversyn Platform': 'Terms specific to how the Adversyn Trading platform works.',
  'Market Structure': 'How orders, books, and venues are wired together.',
  'Technical': 'Price- and volume-based concepts you will see on charts.',
  'Fundamental': 'Performance and accounting concepts used to evaluate positions.',
  'Options': 'Concepts that only apply when you are trading options.',
  'Risk': 'How traders size positions, cap losses, and measure exposure.',
};

export default function GlossaryModule() {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('All');

  const normalizedQuery = query.trim().toLowerCase();

  const grouped = useMemo(() => {
    const filtered = GLOSSARY.filter((item) => {
      const matchesCategory =
        activeCategory === 'All' || item.category === activeCategory;
      if (!matchesCategory) return false;
      if (!normalizedQuery) return true;
      const hay = [item.term, item.definition, item.plain ?? '']
        .join(' ')
        .toLowerCase();
      return hay.includes(normalizedQuery);
    });

    const order = TRADING_TERM_CATEGORIES;
    const byCategory: Record<string, typeof GLOSSARY> = {};
    for (const cat of order) byCategory[cat] = [];
    for (const item of filtered) byCategory[item.category].push(item);
    for (const cat of order) {
      byCategory[cat].sort((a, b) => a.term.localeCompare(b.term));
    }
    return order
      .map((cat) => ({ category: cat as TradingTermCategory, items: byCategory[cat] }))
      .filter((g) => g.items.length > 0);
  }, [activeCategory, normalizedQuery]);

  const totalMatches = grouped.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className="app-container space-y-8 pb-20" data-testid="glossary-module">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display flex items-center gap-3">
            <BookOpen size={32} className="text-emerald-600" />
            Trading Glossary
          </h1>
          <p className="text-[var(--muted-text)] mt-1 font-medium">
            Plain-English definitions for every term you will run into across the
            Adversyn Trading platform. Grouped by category, with a quick search
            and filter so you can find what you need in seconds.
          </p>
        </div>
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-text)]"
            size={16}
          />
          <input
            type="text"
            data-testid="glossary-input-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search glossary..."
            className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl pl-10 pr-10 py-2 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 transition-all w-72 shadow-sm placeholder:text-[var(--muted-text)] uppercase italic"
          />
          {query && (
            <button
              type="button"
              data-testid="glossary-btn-clear-search"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted-text)] hover:text-emerald-600 transition-colors"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="glossary-filter-bar">
        {(['All', ...TRADING_TERM_CATEGORIES] as CategoryFilter[]).map((cat) => {
          const isActive = activeCategory === cat;
          return (
            <button
              key={cat}
              type="button"
              data-testid={`glossary-filter-${cat.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => {
                logInteraction('Glossary', 'Filter Category', { category: cat });
                setActiveCategory(cat);
              }}
              className={cn(
                'px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border transition-colors',
                isActive
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                  : 'bg-[var(--card-bg)] text-[var(--muted-text)] border-[var(--card-border)] hover:text-emerald-600 hover:border-emerald-600/40',
              )}
            >
              {cat}
            </button>
          );
        })}
      </div>

      <div
        className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest"
        data-testid="glossary-result-count"
      >
        {totalMatches === 0
          ? 'No matches'
          : `${totalMatches} ${totalMatches === 1 ? 'term' : 'terms'}`}
        {normalizedQuery ? ` matching "${query.trim()}"` : ''}
        {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
      </div>

      {grouped.length > 1 && (
        <nav
          className="flex flex-wrap gap-x-4 gap-y-2 text-[10px] font-black uppercase tracking-widest"
          data-testid="glossary-jump-nav"
          aria-label="Jump to category"
        >
          <span className="text-[var(--muted-text)]">Jump to:</span>
          {grouped.map(({ category, items }) => {
            const slug = category.replace(/\s+/g, '-').toLowerCase();
            return (
              <a
                key={category}
                href={`#glossary-cat-${slug}`}
                data-testid={`glossary-jump-${slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  logInteraction('Glossary', 'Jump To Category', { category });
                  document
                    .getElementById(`glossary-cat-${slug}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="text-emerald-600 hover:underline"
              >
                {category}
                <span className="ml-1 text-[var(--muted-text)]">({items.length})</span>
              </a>
            );
          })}
        </nav>
      )}

      {totalMatches === 0 ? (
        <div
          className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-10 text-center shadow-sm"
          data-testid="glossary-empty-state"
        >
          <BookOpen size={32} className="text-[var(--muted-text)] mx-auto mb-3 opacity-60" />
          <h3 className="text-base font-black text-[var(--app-text)] uppercase italic mb-1">
            No matching terms
          </h3>
          <p className="text-xs text-[var(--muted-text)] font-medium">
            Try a different search, or clear the category filter.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(({ category, items }) => (
            <section
              key={category}
              id={`glossary-cat-${category.replace(/\s+/g, '-').toLowerCase()}`}
              data-testid={`glossary-section-${category.replace(/\s+/g, '-').toLowerCase()}`}
              className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-sm overflow-hidden scroll-mt-24"
            >
              <header className="px-6 py-4 border-b border-[var(--card-header-border)] bg-[var(--card-header-bg)] flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-black text-[var(--app-text)] uppercase tracking-widest italic">
                    {category}
                  </h2>
                  <p className="text-[11px] text-[var(--muted-text)] font-medium mt-0.5">
                    {CATEGORY_DESCRIPTIONS[category]}
                  </p>
                </div>
                <span className="text-[10px] font-black px-2 py-0.5 bg-emerald-600/10 text-emerald-600 rounded uppercase tracking-widest shrink-0">
                  {items.length} {items.length === 1 ? 'term' : 'terms'}
                </span>
              </header>
              <ul className="divide-y divide-[var(--card-header-border)]">
                {items.map((item) => (
                  <li
                    key={`${category}-${item.term}`}
                    data-testid="glossary-term"
                    className="px-6 py-4 hover:bg-[var(--input-bg)]/40 transition-colors"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic">
                        {item.term}
                      </h3>
                      <span className="text-[9px] font-black px-1.5 py-0.5 bg-[var(--input-bg)] text-[var(--muted-text)] rounded uppercase tracking-widest shrink-0">
                        {item.category}
                      </span>
                    </div>
                    {item.plain && (
                      <p className="mt-1 text-[12px] text-[var(--app-text)] font-medium leading-relaxed">
                        {item.plain}
                      </p>
                    )}
                    {item.term === 'AI Council' && (
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                        <span className="inline-flex items-center gap-1.5"><BrainIcon size={18} /> GPT</span>
                        <span className="inline-flex items-center gap-1.5"><OwlIcon size={18} /> Claude</span>
                        <span className="inline-flex items-center gap-1.5"><ElephantIcon size={18} /> Gemini</span>
                        <span className="inline-flex items-center gap-1.5"><CompassIcon size={18} /> Perplexity</span>
                      </div>
                    )}
                    <p
                      className={cn(
                        'mt-1 text-[11px] text-[var(--muted-text)] font-medium leading-relaxed',
                        item.plain && 'opacity-80',
                      )}
                    >
                      <span className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)] mr-1">
                        Formal:
                      </span>
                      {item.definition}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
